import { z } from "zod";

import { paginationSchema } from "~/services/schemas";

// Backing values of an activity's outcome (what the BE emits / the filter
// matches on). Kept as a const so the raw strings aren't repeated inline
// across the list filter and the detail view.
export const ACTIVITY_OUTCOME = {
  Success: "success",
  Failed: "failed",
} as const;

const activityUserSchema = z
  .object({
    id: z.string(),
    firstName: z.string(),
    lastName: z.string(),
    email: z.string(),
  })
  .nullable();

const activityDataChangesSchema = z
  .object({
    old: z.record(z.string(), z.unknown()).nullable(),
    new: z.record(z.string(), z.unknown()).nullable(),
  })
  .nullable();

export const activitySchema = z.object({
  id: z.number(),
  eventType: z.string(),
  activityCategory: z.string(),
  // Nullable: a legacy/malformed `properties` payload falls back to null
  // server-side rather than crashing the read (see Activity::tryFromEnum).
  action: z.string().nullable(),
  source: z.string().nullable(),
  outcome: z.string(),
  outcomeDetail: z.string().nullable(),
  // IP is intentionally not part of the list response — it's PII the list
  // UI never renders. It's only exposed on the (future) detail endpoint.
  dataChanges: activityDataChangesSchema,
  occurredAt: z.string(),
  recordedAt: z.string().nullable(),
  createdAt: z.string(),
  subject: activityUserSchema,
  causer: activityUserSchema,
});

// This endpoint responds via Flugg Responder (`responder()->success()`), not a
// Laravel API Resource collection, so the envelope is `data` + `pagination`
// (already camelCase) rather than the native Laravel `data` + `meta` + `links`
// shape used by Resource-based endpoints (see ~/api ServicePagination). The
// shape matches the shared `paginationSchema` in ~/services/schemas exactly
// (count/total/perPage/currentPage/totalPages + optional links.next/previous),
// so it's reused here instead of being redefined.
export const activitiesResponseSchema = z.object({
  data: z.array(activitySchema),
  pagination: paginationSchema,
});

// Detail-only fields (ip, userAgent) are intentionally excluded from
// activitySchema above — the list view never renders them (see the BE
// ActivityTransformer class doc). ActivityDetailTransformer adds them for the
// single-resource endpoint only.
export const activityDetailSchema = activitySchema.extend({
  ip: z.string().nullable(),
  userAgent: z.string().nullable(),
});

// Single-resource Flugg envelope: `data` only, no `pagination`.
export const activityDetailResponseSchema = z.object({
  data: activityDetailSchema,
});
